// FILE: /src/Components/BuySubscription/BuySubscription.js (FULLY UPDATED & WARNINGS FIXED)

import React, { useEffect, useState, useCallback, useMemo } from "react";
import { createSubscription, fetchUserProfile, getPlans, checkUserSubscriptionStatus } from "../services/user.profile";
import { LoadingOutlined, CloseOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { message, Spin, Button, Switch, Collapse } from "antd";
import { useNavigate } from "react-router-dom";

// --- Helper: Feature Icon ---
const FeatureIcon = ({ included }) => (
<i className={`fa-solid fa-check-circle text-lg mr-3 flex-shrink-0 ${included ? 'text-green-500' : 'text-gray-300'}`}></i>
);

// --- Helper: SocialChannelIcons ---
const SocialChannelIcons = ({ channels = [] }) => {
const iconMap = {
Facebook: 'fa-facebook text-blue-600',
Instagram: 'fa-instagram text-pink-500',
Pinterest: 'fa-pinterest text-red-600',
X: 'fa-twitter text-black',
LinkedIn: 'fa-linkedin text-blue-700',
YouTube: 'fa-youtube text-red-600',
'Google Business': 'fa-google text-red-500',
Tiktok: 'fa-tiktok text-black'
};
if (channels.length === 0) return null;
return (<div className="flex items-center gap-3">{channels.map(channel => (iconMap[channel] && <i key={channel} className={`fa-brands ${iconMap[channel]} text-xl`} title={channel}></i>))}</div>);
};

// --- Child Component: Plan Card ---
const PlanCard = ({ plan, allPlans, onSelect, subscribingPlan, billingCycle, currentUserPlan }) => {
const [showMax, setShowMax] = useState(false);

const maxPlanVariant = useMemo(() =>
allPlans.find(p => p.base_plan_name === `${plan.base_plan_name} Max`),
[allPlans, plan.base_plan_name]
);

const isPopular = plan.base_plan_name === "Growth";
const isFree = plan.isFree;
const isCustom = plan.isCustom;

const displayData = useMemo(() => {
if (isFree || isCustom) return plan.monthly;
const targetPlan = showMax && maxPlanVariant ? maxPlanVariant : plan;
return billingCycle === 'yearly' && targetPlan.yearly ? targetPlan.yearly : targetPlan.monthly;
}, [plan, maxPlanVariant, billingCycle, isFree, isCustom, showMax]);

const isCurrentPlan = useMemo(() => {
if (!currentUserPlan || !displayData) return false;
if (isFree && currentUserPlan.plan_id === 'free_plan') return true;
return displayData.plan_id === currentUserPlan.plan_id;
}, [currentUserPlan, displayData, isFree]);

const discount = useMemo(() => {
if (isFree || isCustom || !plan.monthly || !plan.yearly) return 0;
const monthlyPrice = plan.monthly.plan_price;
const yearlyPricePerMonth = plan.yearly.plan_price / 12;
if (monthlyPrice === 0) return 0;
return Math.round(((monthlyPrice - yearlyPricePerMonth) / monthlyPrice) * 100);
}, [plan, isFree, isCustom]);

const convertInrToUsd = (inr, isPerMonth = true) => {
const usdPrice = (inr / 100) / 83;
return isPerMonth ? Math.round(usdPrice) : usdPrice.toFixed(2);
};

const getPriceDetails = () => {
const priceInPaise = displayData.plan_price || 0;
const isYearly = billingCycle === 'yearly';

const monthlyPriceInr = isYearly ? priceInPaise / 12 : priceInPaise;

const actualUsdPrice = convertInrToUsd(monthlyPriceInr, true);
const inflatedUsdPrice = actualUsdPrice * 10;

return {
actualUsdPrice,
inflatedUsdPrice,
billedAnnuallyUsd: convertInrToUsd(priceInPaise, false)
};
};

const priceDetails = getPriceDetails();

const getButtonText = () => {
if (isCurrentPlan) return "Current Plan";
if (isFree) return "Get Started";
if (isCustom) return "Contact Sales";
if (!currentUserPlan) return "Choose Plan";
const currentPrice = currentUserPlan.plan?.plan_price || 0;
const displayPrice = displayData.plan_price;
if (displayPrice > currentPrice) return "Upgrade";
if (displayPrice < currentPrice) return "Downgrade";
return "Choose Plan";
};

if (!displayData) return null;

return (
<div className={`relative flex flex-col p-6 bg-white rounded-2xl shadow-lg border-2 transition-all duration-300 h-full ${isPopular ? 'border-brand-primary' : 'border-gray-200'} ${isCurrentPlan ? 'border-green-500 ring-2 ring-green-200' : 'hover:border-gray-300 hover:-translate-y-1'}`}>
{isPopular && !isCurrentPlan && (<div className="absolute top-0 -translate-y-1/2 left-1/2 -translate-x-1/2 px-4 py-1 text-sm font-semibold text-white bg-brand-primary rounded-full shadow-md">Most Popular</div>)}
{isCurrentPlan && (<div className="absolute top-0 -translate-y-1/2 left-1/2 -translate-x-1/2 px-4 py-1 text-sm font-semibold text-white bg-green-600 rounded-full shadow-md">Current Plan</div>)}
{billingCycle === 'yearly' && !isFree && !isCustom && discount > 0 && (<div className="absolute top-4 right-4 px-3 py-1 text-xs font-bold text-green-800 bg-green-200 rounded-full">Save {discount}%</div>)}

<h3 className="text-2xl font-bold text-neutral-dark mt-4">{plan.base_plan_name}</h3>

{maxPlanVariant && (
<div className="flex items-center gap-2 my-2 text-sm font-semibold">
<span className={!showMax ? 'text-brand-primary' : 'text-gray-400'}>{plan.base_plan_name}</span>
<Switch checked={showMax} onChange={setShowMax} size="small" />
<span className={showMax ? 'text-brand-primary' : 'text-gray-400'}>{plan.base_plan_name} Max</span>
</div>
)}

<p className="text-neutral-muted mt-2 min-h-[42px] text-sm">{displayData.plan_description}</p>

<div className="my-6">
{isCustom ? (
<span className="text-4xl font-extrabold text-neutral-dark">Custom</span>
) : isFree ? (
<span className="text-5xl font-extrabold text-neutral-dark">$0</span>
) : (
<div className="flex items-baseline gap-2">
<span className="text-2xl font-medium text-gray-400 line-through">${priceDetails.inflatedUsdPrice}</span>
<span className="text-5xl font-extrabold text-neutral-dark">${priceDetails.actualUsdPrice}</span>
<span className="text-lg font-medium text-neutral-muted">/month</span>
</div>
)}
{billingCycle === 'yearly' && !isFree && !isCustom && (
<p className="text-xs text-neutral-muted mt-1">
Billed annually at ${priceDetails.billedAnnuallyUsd}
</p>
)}
</div>

<ul className="space-y-4 flex-grow text-sm text-neutral-dark">
<li className="flex items-start"><FeatureIcon included={true} /> {displayData.brands_limit} Brand{displayData.brands_limit === 1 ? '' : 's'} Included</li>
<li className="flex items-start"><FeatureIcon included={true} /> {displayData.posts_per_month_limit} Scheduled Posts/Month</li>
<li className="flex items-start"><FeatureIcon included={true} /> Up to {displayData.profiles_limit} Social Channels</li>
<li className="flex items-start"><FeatureIcon included={true} /> {displayData.storage_gb_limit} GB Asset Library / Brand</li>
<li className="flex items-start"><FeatureIcon included={true} /> {displayData.ai_credits_total_limit} AI Credits / Month</li>
<li className="flex items-start"><FeatureIcon included={true} /> Support: {displayData.support_level || 'Community'}</li>
<li className="flex items-start pt-2"><SocialChannelIcons channels={displayData.supported_channels} /></li>
</ul>

<button
onClick={() => { if (isCustom) { window.open('https://oolook.in/schedule-demo', '_blank'); } else { onSelect(displayData.plan_id); } }}
disabled={!!subscribingPlan || isCurrentPlan}
className={`w-full mt-8 py-3 font-semibold rounded-lg transition-all duration-300 transform ${isCurrentPlan ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : (isPopular ? 'btn-primary shadow-lg hover:shadow-xl' : 'bg-brand-secondary-bg text-brand-secondary-text hover:bg-brand-secondary-hover')} ${subscribingPlan ? 'opacity-50 cursor-wait' : 'hover:-translate-y-1'}`}
>
{subscribingPlan === displayData.plan_id ? <Spin indicator={<LoadingOutlined style={{ fontSize: 24, color: "#fff" }} spin />} /> : getButtonText()}
</button>
</div>
);
};

// --- FAQ Section Component ---
const FAQSection = () => {
const faqData = [
{ key: '1', question: 'What counts as a social profile?', answer: 'Each social media account you connect to Oolook counts as one profile. For example, one Facebook Page, one Instagram account, and one Pinterest account would count as three social profiles.' },
{ key: '2', question: 'Can I change my plan later?', answer: 'Absolutely! You can upgrade, downgrade, or switch between monthly and yearly billing at any time from your account settings. Changes will be prorated.' },
{ key: '3', question: 'What happens if I cancel my subscription?', answer: 'If you cancel your paid subscription, your account will be moved to our Free plan at the end of your current billing cycle. You will retain access to your data but will be subject to the Free plan\'s limitations.' },
{ key: '4', question: 'Do you offer a free trial?', answer: 'We offer a comprehensive Free plan that allows you to explore our core features. This serves as our trial, giving you ample time to see if Oolook is the right fit for you without any time pressure.' },
{ key: '5', question: 'What payment methods do you accept?', answer: 'We accept all major credit cards, debit cards, UPI, and other popular payment methods through our secure payment partner, Razorpay.' },
{ key: '6', question: 'What are AI credits and how do they work?', answer: 'AI credits are used for our content generation features, like creating captions or images. Each plan comes with a monthly allowance of credits, which reset at the start of each billing cycle.' },
{ key: '7', question: 'Is my data safe with Oolook?', answer: 'Yes, data security is our top priority. We use industry-standard encryption for data in transit and at rest. All connections to social media platforms use official, secure APIs.' },
{ key: '8', question: 'Can I add multiple team members to my account?', answer: 'Team member functionality is available on our Pro and Enterprise plans, allowing you to collaborate seamlessly with your colleagues.' },
{ key: '9', question: 'What kind of support do you offer?', answer: 'Support levels vary by plan. All users have access to our help center. Paid plans include email and chat support, while our Enterprise plan offers a dedicated account manager.' },
{ key: '10', question: 'Do you have a plan for large agencies or enterprises?', answer: 'Yes! Our Enterprise plan is fully customizable to meet the needs of large teams and agencies. Please contact our sales team to discuss a bespoke solution.' },
];

const items = faqData.map(faq => ({
key: faq.key,
label: <span className="font-semibold text-neutral-dark">{faq.question}</span>,
children: <p className="text-neutral-muted">{faq.answer}</p>,
className: "bg-white mb-4 rounded-lg shadow-sm border"
}));

return (
<div className="w-full max-w-4xl mx-auto mt-20">
<h2 className="text-3xl font-extrabold text-neutral-dark text-center mb-8">Frequently Asked Questions</h2>
<Collapse items={items} accordion bordered={false} className="bg-transparent" />
</div>
);
};

// --- Main Subscription Component ---
export default function BuySubscription() {
const [userData, setUserData] = useState({});
const [allPlans, setAllPlans] = useState([]);
const [currentUserPlan, setCurrentUserPlan] = useState(null);
const [loading, setLoading] = useState(true);
const [isVerifying, setIsVerifying] = useState(false);
const [subscribingPlan, setSubscribingPlan] = useState(null);
const [subscriptionId, setSubscriptionId] = useState("");
const [billingCycle, setBillingCycle] = useState('monthly');
const [isChangingPlan, setIsChangingPlan] = useState(false);
const navigate = useNavigate();

const initialize = useCallback(async () => {
try {
setLoading(true);
const { user, subscription } = await fetchUserProfile();
setUserData(user);

if (subscription && (subscription.status === 'active' || subscription.status === 'created')) {
setCurrentUserPlan(subscription);
if (subscription.status === 'active') {
setIsChangingPlan(true);
}
}

const plansResponse = await getPlans();
if (plansResponse.success) {
setAllPlans(plansResponse?.plans || []);
} else {
message.error("Failed to fetch subscription plans.");
}
} catch (error) {
message.error("Could not load user data or plans. Please try again.");
} finally {
setLoading(false);
}
}, []);

useEffect(() => {
initialize();
}, [initialize]);

const processedPlans = useMemo(() => {
    if (!allPlans || allPlans.length === 0) return [];

    // Separate the special plans
    const freePlan = allPlans.find(p => p.isFree);
    const enterprisePlan = allPlans.find(p => p.isCustom);
    
    // Get all other standard, paid plans
    const standardPlans = allPlans.filter(p => !p.isFree && !p.isCustom && !p.base_plan_name.endsWith(" Max"));

    // Sort the standard plans alphabetically by their base name
    standardPlans.sort((a, b) => a.base_plan_name.localeCompare(b.base_plan_name));
    
    // Construct the final sorted array
    const finalOrder = [];
    if (freePlan) finalOrder.push(freePlan);
    finalOrder.push(...standardPlans);
    if (enterprisePlan) finalOrder.push(enterprisePlan);

    return finalOrder;
}, [allPlans]);

const startPollingForSubscriptionStatus = useCallback(() => {
setIsVerifying(true);
let pollCount = 0;
const maxPolls = 15; 

const pollInterval = setInterval(async () => {
pollCount++;
try {
const status = await checkUserSubscriptionStatus();
if (status.isActivePlan && status.subscriptionStatus === 'active') {
clearInterval(pollInterval);
message.success("Subscription verified! Refreshing your account...");
window.location.href = isChangingPlan ? "/Dashboard/AccountSetting" : "/Dashboard";
} else if (pollCount >= maxPolls) {
clearInterval(pollInterval);
setIsVerifying(false);
message.warning("Verification is taking a moment. We'll update your account status shortly.", 10);
navigate(isChangingPlan ? "/Dashboard/AccountSetting" : "/Dashboard");
}
} catch (error) {
clearInterval(pollInterval);
setIsVerifying(false);
message.error("An error occurred during verification. Please check your billing page or contact support.");
navigate("/Dashboard/AccountSetting");
}
}, 2000); 
}, [navigate, isChangingPlan]);

const handleSubBuy = async (plan_id) => {
    if (!userData?.email_id) {
        message.error("User data not loaded. Please refresh the page.");
        return;
    }

    if (plan_id === "free_plan") {
        setSubscribingPlan('free_plan');
        try {
            const data = await createSubscription({
                email_id: userData.email_id,
                plan_id: 'free_plan',
                razorpay_customer_id: userData.razorpay_customer_id,
            });

            if (data?.success) {
                message.success("Successfully changed to Free Plan! Refreshing your account...");
                setTimeout(() => {
                    window.location.href = isChangingPlan ? "/Dashboard/AccountSetting" : "/Dashboard";
                }, 1500);
            }
        } catch (error) {
            message.error(error.message || "Failed to change plan.");
        } finally {
            setSubscribingPlan(null);
        }
        return;
    }

    setSubscribingPlan(plan_id);
    try {
        const data = await createSubscription({
            email_id: userData.email_id,
            firstName: userData.first_name,
            contact: userData.mobile,
            razorpay_customer_id: userData.razorpay_customer_id,
            plan_id,
        });
        
        if (data?.subscription?.id) {
            setSubscriptionId(data.subscription.id);
        } else {
            throw new Error(data?.error || "Subscription failed.");
        }
    } catch (error) {
        const errorMessage = error.response?.data?.error || "Unable to subscribe. Please try again.";
        message.error(errorMessage);
    } finally {
        setSubscribingPlan(null);
    }
};

const handlePayment = useCallback(async () => {
const options = {
key: process.env.REACT_APP_RAZORPAY_KEY_ID,
subscription_id: subscriptionId,
name: "Oolook",
description: "Subscription",
handler: async () => {
startPollingForSubscriptionStatus();
},
prefill: { email: userData.email_id, contact: userData.mobile },
theme: { color: "#6D28D9" },
};
const rzp = new window.Razorpay(options);
rzp.open();
}, [subscriptionId, userData, startPollingForSubscriptionStatus]);

useEffect(() => {
if (subscriptionId) {
handlePayment();
}
}, [subscriptionId, handlePayment]);

return (
<div className="min-h-screen bg-gradient-to-b from-purple-50 via-blue-50 to-white flex flex-col items-center p-6 sm:p-10">
{isVerifying && (<div className="fixed inset-0 bg-white/80 backdrop-blur-sm flex flex-col items-center justify-center z-50"><Spin size="large" /><p className="mt-4 text-lg font-semibold text-gray-700">Verifying your payment, please wait...</p><p className="text-sm text-gray-500">This may take a few moments. Your page will refresh automatically.</p></div>)}
{isChangingPlan && (<div className="w-full max-w-7xl mb-6 flex justify-between items-center"><Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>Back to Billing</Button><Button type="text" shape="circle" icon={<CloseOutlined />} onClick={() => navigate('/Dashboard/AccountSetting')} /></div>)}
{loading ? (<div className="flex items-center justify-center h-screen"><Spin size="large" /></div>) : (
<div className="w-full max-w-7xl">
<div className="text-center mb-12">
<h1 className="text-4xl md:text-5xl font-extrabold text-neutral-dark tracking-tight">{isChangingPlan ? 'Change Your Plan' : 'Choose Your Plan'}</h1>
<p className="text-lg text-neutral-muted mt-4 max-w-2xl mx-auto">Select the perfect plan to superpower your social media management and grow your brand.</p>
</div>

<div className="flex justify-center items-center mb-10">
<div className="p-1 bg-gray-200 rounded-full flex items-center">
<button onClick={() => setBillingCycle('monthly')} className={`px-6 py-2 text-sm font-semibold rounded-full transition-all ${billingCycle === 'monthly' ? 'bg-white shadow' : 'text-gray-500'}`}>
Monthly
</button>
<button onClick={() => setBillingCycle('yearly')} className={`px-6 py-2 text-sm font-semibold rounded-full transition-all ${billingCycle === 'yearly' ? 'bg-white shadow' : 'text-gray-500'}`}>
Yearly
</button>
</div>
</div>

<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto items-start">
{processedPlans.map((plan) => (
<PlanCard
key={plan.base_plan_name}
plan={plan}
onSelect={handleSubBuy}
subscribingPlan={subscribingPlan}
billingCycle={billingCycle}
currentUserPlan={currentUserPlan}
allPlans={allPlans}
/>
))}
</div>
<FAQSection />
</div>
)}
</div>
);
}